'use client'

import * as React from 'react'
import { CircleAlert, CircleCheck, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

export type SlugTone = 'muted' | 'checking' | 'positive' | 'danger'

/**
 * The network's address: the fixed `alumnia.net/in/` prefix and the slug in
 * one control, with its availability underneath (ALU-118). Used by `/start`
 * and by the organization step of the admin invitation.
 *
 * The status line is a polite live region, so "Revisando…" and "Está
 * disponible." are announced as the person types; a validation error takes
 * its place and marks the input `aria-invalid`.
 */
export const SlugField = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'input'>, 'className' | 'prefix'> & {
    label: string
    prefix: string
    help?: string
    /** What the availability check says, and how it reads. */
    status?: { tone: SlugTone; text: string } | null
    error?: string
  }
>(function SlugField({ label, prefix, help, status, error, id: idProp, ...input }, ref) {
  const autoId = React.useId()
  const id = idProp ?? autoId
  const helpId = help ? `${id}-help` : undefined
  const statusId = `${id}-status`
  const describedBy = [helpId, statusId].filter(Boolean).join(' ')

  return (
    <div data-slot="slug-field" className="grid min-w-0 content-start gap-2 text-en-ink">
      <label htmlFor={id} className="text-en-label">
        {label}
      </label>
      {help && (
        <p id={helpId} className="text-en-meta text-en-ink-muted">
          {help}
        </p>
      )}
      <div
        className={cn(
          'flex min-h-en-control min-w-0 items-stretch overflow-hidden rounded-en-md border bg-en-surface',
          'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-solid focus-within:outline-en-focus',
          error || status?.tone === 'danger' ? 'border-en-danger' : 'border-en-line-control'
        )}
      >
        <span
          aria-hidden
          className="flex shrink-0 items-center border-r border-en-line bg-en-surface-sunken px-3 text-en-meta text-en-ink-muted"
        >
          {prefix}
        </span>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby={describedBy}
          aria-invalid={error || status?.tone === 'danger' ? true : undefined}
          className="min-w-0 flex-1 bg-transparent px-3 py-2 font-en-sans text-en-body text-en-ink outline-none placeholder:text-en-ink-muted"
          {...input}
        />
      </div>
      <p
        id={statusId}
        aria-live="polite"
        className={cn(
          'flex min-h-5 items-start gap-2 text-en-meta',
          error || status?.tone === 'danger'
            ? 'text-en-danger'
            : status?.tone === 'positive'
              ? 'text-en-positive'
              : 'text-en-ink-muted'
        )}
      >
        {error ? (
          <>
            <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
            <span>{error}</span>
          </>
        ) : status ? (
          <>
            {status.tone === 'checking' && (
              <Loader2
                aria-hidden
                className="mt-px size-4 shrink-0 animate-spin motion-reduce:animate-none"
              />
            )}
            {status.tone === 'positive' && (
              <CircleCheck aria-hidden className="mt-px size-4 shrink-0" />
            )}
            {status.tone === 'danger' && (
              <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
            )}
            <span>{status.text}</span>
          </>
        ) : null}
      </p>
    </div>
  )
})
